// src/pages/dataInput/sources/DBSource.tsx
import React, { useEffect, useState } from "react";
import { cls } from "../../theme/theme";
import { ConnectionManagerModal, SelectedSource } from "../../connection/ConnectionManagerModal";
import { SourcePayload, SheetMode, Relationship } from "../types";

const CONNECTOR_ICONS: Record<string, string> = {
  postgresql: "🐘", snowflake: "❄️", aws_glue: "☁️",
  mysql: "🐬", redshift: "🔴", bigquery: "📊", databricks: "🧱",
};

interface Props {
  initialSource?:    SelectedSource | null;
  initialSheetMode?: SheetMode;
  onChange: (payload: SourcePayload | null) => void;
}

export const DBSource: React.FC<Props> = ({
  initialSource     = null,
  initialSheetMode  = "sub_entities",
  onChange,
}) => {
  const [showModal,      setShowModal]      = useState(false);
  const [selectedSource, setSelectedSource] = useState<SelectedSource | null>(initialSource);
  const [sheetMode,      setSheetMode]      = useState<SheetMode>(initialSheetMode);

  useEffect(() => {
    if (!selectedSource) { onChange(null); return; }

    const tables = selectedSource.tables || [];
    const availableTables = tables.map(t => ({
      prefix:  t.toUpperCase().replace(/\s+/g, "_"),
      label:   t,
      columns: selectedSource.columns?.[t] ?? [],
    }));

    const dbRels: Relationship[] = (selectedSource.relationships || []).map(r => ({
      from_table:  r.from_table.toUpperCase().replace(/\s+/g, "_"),
      from_column: r.from_column,
      to_table:    r.to_table.toUpperCase().replace(/\s+/g, "_"),
      to_column:   r.to_column,
      source:      "db",
      reason:      "Imported from INFORMATION_SCHEMA / catalog metadata.",
    }));

    onChange({
      kind:          "db",
      label:         `${selectedSource.connection_name} · ${selectedSource.schema}`,
      sheetMode,
      dbSource:      selectedSource,
      availableTables,
      relationships: dbRels,
    });
  }, [selectedSource, sheetMode, onChange]);

  const tableCount = selectedSource?.tables?.length ?? 0;

  return (
    <div>
      {showModal && (
        <ConnectionManagerModal
          onClose={() => setShowModal(false)}
          onSelect={(src: SelectedSource) => setSelectedSource(src)}
        />
      )}

      {!selectedSource ? (
        <div className="gw-source-card" onClick={() => setShowModal(true)}>
          <span className="gw-source-card-icon">🔌</span>
          <div className="gw-source-card-body">
            <div className="gw-source-card-title">Connect to a database or pipeline</div>
            <div className="gw-source-card-sub">
              PostgreSQL · Snowflake · AWS Glue · MySQL · Redshift · BigQuery · Databricks
            </div>
          </div>
          <div className="gw-source-card-cta">Browse →</div>
        </div>
      ) : (
        <div className="gw-source-card connected">
          <span className="gw-source-card-icon">
            {CONNECTOR_ICONS[selectedSource.connector_type] || "🔌"}
          </span>
          <div className="gw-source-card-body">
            <div className="gw-source-card-title">{selectedSource.connection_name}</div>
            <div className="gw-source-card-sub">
              {selectedSource.schema}
              {selectedSource.custom_sql
                ? " · (custom SQL)"
                : tableCount > 1
                  ? ` · ${tableCount} tables`
                  : tableCount === 1
                    ? ` · ${selectedSource.tables[0]}`
                    : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="gw-btn gw-btn-ghost gw-btn-sm" onClick={() => setShowModal(true)}>
              Change
            </button>
            <button className="gw-btn gw-btn-ghost gw-btn-sm" onClick={() => setSelectedSource(null)}>
              Clear
            </button>
          </div>
        </div>
      )}

      {tableCount > 1 && !selectedSource?.custom_sql && (
        <div style={{ marginTop: 14 }}>
          <div className="gw-mode-toggle">
            {(["sub_entities", "separate_entities"] as SheetMode[]).map(m => (
              <button
                key={m}
                className={cls("gw-mode-toggle-btn", sheetMode === m && "active")}
                onClick={() => setSheetMode(m)}
              >
                {m === "sub_entities"
                  ? "Group as one entity"
                  : "One entity per table"}
              </button>
            ))}
          </div>
          <div className="gw-mode-explainer">
            {sheetMode === "sub_entities"
              ? `All ${tableCount} tables will be sub-entities under a single parent entity.`
              : `Each of the ${tableCount} tables becomes its own independent entity.`}
          </div>
        </div>
      )}
    </div>
  );
};

export default DBSource;